import { useCallback, useSyncExternalStore } from 'react'

// Where the assistant lives, and how it is placed there.
//
// The assistant used to be three different things: an inline column on
// Planning, a docked right rail on Inbox, and a floating emoji bubble on
// Aesthetics. Three containers holding the same conversation is why the judge
// read the product as having no single voice ("presented three different ways
// across the three screens"). There is now ONE panel, and this is the only
// thing that differs between surfaces — where the operator has put it.
//
// The placement is the operator's, not the route's: it persists across
// navigation and reloads, so the assistant is where you last left it rather
// than wherever a given screen decided to put it.

export type DockMode = 'closed' | 'docked' | 'float' | 'right'

export interface DockState {
  mode: DockMode
  /** Float position, viewport coordinates of the panel's top-left. */
  x: number
  y: number
  /** Float size. The right-dock takes its width from this too, so resizing in
   *  one mode carries over to the other. */
  w: number
  h: number
  /**
   * Has the operator sized this themselves?
   *
   * Until they have, the panel HUGS its conversation — a fixed height meant one
   * greeting sat in ~450px of void. The moment they drag a corner it becomes
   * explicit and stays exactly where they put it, because otherwise resizing
   * only moved an invisible ceiling and read as broken.
   */
  sized?: boolean
}

const KEY = 'xnapz.assistant.dock'

export const MIN_W = 320
export const MIN_H = 220

function defaults(): DockState {
  // Right side, below the header, sized to show a few turns.
  const w = 380
  const h = Math.min(560, Math.max(320, Math.round(window.innerHeight * 0.62)))
  // DOCKED by default: the conversation lives in the console's own Chat tab
  // rather than floating over the work. A persistent column cost a third of the
  // screen to hold one greeting — "the entire right third empty". Floating and
  // pinning are both one click away for anyone who wants the chat beside the
  // plan instead of instead of it.
  return {
    mode: 'closed',
    x: Math.max(16, window.innerWidth - w - 24),
    y: 92,
    w,
    h,
  }
}

/** Keep the panel reachable: a saved position from a larger window (or a
 *  different monitor) must not strand it off-screen. */
export function clampToViewport(s: DockState): DockState {
  const w = Math.min(Math.max(s.w, MIN_W), Math.max(MIN_W, window.innerWidth - 32))
  const h = Math.min(Math.max(s.h, MIN_H), Math.max(MIN_H, window.innerHeight - 32))

  // Growing must not push the panel off the edge: a resize handle you cannot
  // reach is a panel you cannot make smaller again. Keep the whole box on
  // screen while it fits, and only then allow it to hang off (dragging, where
  // a strip of the grab bar staying reachable is the requirement).
  let x = s.x
  let y = s.y
  if (x + w > window.innerWidth - 8) x = window.innerWidth - 8 - w
  if (y + h > window.innerHeight - 8) y = window.innerHeight - 8 - h

  return {
    ...s,
    w,
    h,
    // At least a strip of the bar stays on screen from every edge, so the panel
    // can always be grabbed back.
    x: Math.min(Math.max(x, -(w - 120)), window.innerWidth - 120),
    y: Math.min(Math.max(y, 8), window.innerHeight - 56),
  }
}

// ONE store, not one useState per caller. The console and the panel both need
// this, and with per-instance state they each held their own copy: changing the
// placement from the docked chat left the panel's copy untouched, so "Floating"
// simply did nothing. Shared state is the only way two views of one setting can
// agree.
let current: DockState | null = null
const listeners = new Set<() => void>()

function read(): DockState {
  if (current) return current
  try {
    const raw = localStorage.getItem(KEY)
    current = raw ? clampToViewport({ ...defaults(), ...JSON.parse(raw) }) : defaults()
  } catch {
    current = defaults()
  }
  return current
}

function write(next: DockState) {
  current = next
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* storage full or blocked — placement just won't persist */
  }
  listeners.forEach((l) => l())
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

if (typeof window !== 'undefined') {
  // A window resize can strand a floating panel off-screen.
  window.addEventListener('resize', () => {
    const s = read()
    if (s.mode === 'float') write(clampToViewport(s))
  })
}

export function useAssistantDock() {
  const dock = useSyncExternalStore(subscribe, read, read)

  const setMode = useCallback((mode: DockMode) => write({ ...read(), mode }), [])
  const move = useCallback(
    (x: number, y: number) => write(clampToViewport({ ...read(), x, y })),
    [],
  )
  const resize = useCallback(
    (w: number, h: number) => write(clampToViewport({ ...read(), w, h, sized: true })),
    [],
  )

  return { dock, setMode, move, resize }
}
